/**
 * I3: Feature Gate Component
 * 
 * Wrapper component that conditionally renders children based on feature flag state.
 * Used for:
 * - Hiding nav items
 * - Gating route components
 * - Conditionally showing UI elements
 * 
 * Admin Bypass: Admins always see gated content regardless of flag state.
 */
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useFeatureFlag, useFeatureFlags } from '../../contexts/FeatureFlagContext';
import { useAuth } from '../../contexts/AuthContext';

/**
 * FeatureGate - Shows children only if feature flag is enabled
 * 
 * Props:
 * - flag: string - Feature flag key (e.g., 'messaging', 'peer_reviews')
 * - children: ReactNode - Content to show if flag is enabled
 * - fallback: ReactNode (optional) - Content to show if flag is disabled
 * - redirectTo: string (optional) - If provided, redirects instead of showing fallback
 * 
 * Usage:
 *   <FeatureGate flag="messaging">
 *     <MessagingComponent />
 *   </FeatureGate>
 * 
 *   <FeatureGate flag="messaging" redirectTo="/feature-not-available">
 *     <MessagingPage />
 *   </FeatureGate>
 */
export const FeatureGate = ({ flag, children, fallback = null, redirectTo = null }) => {
  const { user } = useAuth();
  const isEnabled = useFeatureFlag(flag);
  
  // Admin bypass: admins always see gated content
  const isAdmin = user?.is_admin || user?.is_superadmin;
  
  if (isEnabled || isAdmin) {
    return <>{children}</>;
  }
  
  // Feature is disabled for this user
  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }
  
  return <>{fallback}</>;
};

/**
 * FeatureGatedRoute - Route-level feature gate that redirects to Not Available page
 * 
 * Props:
 * - flag: string - Feature flag key
 * - children: ReactNode - Route component to render if flag is enabled
 * 
 * Usage in App.js:
 *   <Route path="/employee/messages" element={
 *     <FeatureGatedRoute flag="messaging">
 *       <EmployeeMessages />
 *     </FeatureGatedRoute>
 *   } />
 */
export const FeatureGatedRoute = ({ flag, children }) => {
  return (
    <FeatureGate flag={flag} redirectTo="/feature-not-available">
      {children}
    </FeatureGate>
  );
};

/**
 * useNavItemVisibility - Hook to determine which nav items should be visible
 * 
 * Returns a function that takes a nav item config and returns true if it should be shown.
 * Admins see all items regardless of flag state.
 * 
 * Usage:
 *   const shouldShowNavItem = useNavItemVisibility();
 *   const visibleLinks = links.filter(shouldShowNavItem);
 */
export const useNavItemVisibility = () => {
  const { user } = useAuth();
  const { isFeatureEnabled, loading } = useFeatureFlags();
  
  const isAdmin = user?.is_admin || user?.is_superadmin;
  
  return (navItem) => {
    // If no flag restriction, always show
    if (!navItem.featureFlag) {
      return true;
    }
    
    // Admin bypass: admins see all items
    if (isAdmin) {
      return true;
    }
    
    // While loading, hide flagged items (fail-closed)
    if (loading) {
      return false;
    }
    
    // Check if feature is enabled
    return isFeatureEnabled(navItem.featureFlag);
  };
};

export default FeatureGate;
